import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";
import australiaSvg from "../../assets/Australia.svg";
import newZealandSvg from "../../assets/NewZealand.svg";
import ukSvg from "../../assets/UnitedKingdom.svg";
import canadaSvg from "../../assets/canada.svg";
import europeSvg from "../../assets/Europe.svg";
import { Button } from "../../components/ui/Button";
import { openWhatsApp } from "../../utils/whatsapp";
import { SEO } from "../../components/SEO";
import { useEffect } from "react";

export default function StudyAbroadPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const destinations = [
    {
      country: "Australia",
      image: australiaSvg,
      description:
        "Turn your academic dreams into global success in Australia. With world-class universities, internationally recognized degrees, generous work-while-studying opportunities, and post-study work pathways, Australia offers international students the perfect balance of quality education and real-world experience. Study in a safe, multicultural environment and graduate with skills that open doors worldwide.",
    },
    {
      country: "New Zealand",
      image: newZealandSvg,
      description:
        "Discover a world-class education in one of the safest and most welcoming countries in the world. New Zealand offers internationally recognized degrees, flexible work opportunities for students, and post-study work pathways that support long-term career goals. With innovative teaching, practical learning, and a high quality of life, studying in New Zealand prepares you for global success while you enjoy a balanced and inspiring lifestyle.",
    },
    {
      country: "UK",
      image: ukSvg,
      description:
        "The United Kingdom is a top destination for international students, offering world-renowned universities, globally respected qualifications, and a rich academic heritage. UK programs are shorter in duration, helping students graduate faster and save costs. International students benefit from post-study work opportunities, diverse career pathways, and a multicultural environment that supports academic excellence and global exposure.",
    },
    {
      country: "Canada",
      image: canadaSvg,
      description:
        "Canada is one of the most student-friendly countries in the world, known for its high-quality education system and strong post-graduation opportunities. International students enjoy affordable tuition, the ability to work while studying, and clear pathways to permanent residency after graduation. With a safe environment, multicultural society, and globally recognized institutions, Canada offers an ideal balance of education, career growth, and quality of life.",
    },
    {
      country: "Europe",
      image: europeSvg,
      description:
        "Europe offers international students access to high-quality education at affordable costs, with many countries providing low or tuition-free study options. Students benefit from globally recognized degrees, diverse cultures, and strong academic traditions. Popular destinations such as Germany, France, Poland, Hungary, and the Netherlands provide excellent education, work opportunities, and pathways to long-term career development. In addition, students studying in Europe enjoy Schengen zone access, allowing seamless travel across multiple European countries for study, tourism, and cultural exposure making Europe an exceptional destination for both education and international experience.",
    },
  ];

  const supportServices = [
    {
      title: "Course & university selection",
      description:
        "We assist you in choosing the right program and university that align with your interests, career goals, and lifestyle, taking into account course content, reputation, costs, and opportunities.",
    },
    {
      title: "Application processing",
      description:
        "We assist you in preparing and submitting your university applications accurately and on time, ensuring all required documents, forms, and guidelines are correctly completed to maximize your chances of admission.",
    },
    {
      title: "Scholarship guidance",
      description:
        "We help you explore available scholarships, understand eligibility criteria, and prepare strong applications to increase your chances of receiving financial support for your studies.",
    },
    {
      title: "Visa filing",
      description:
        "We guide you through the entire visa process, from document preparation to submission, ensuring compliance with all requirements and reducing the risk of delays or rejections.",
    },
    {
      title: "Accommodation assistance",
      description:
        "We assist you in finding safe, convenient, and affordable housing options near your university, helping with bookings and understanding local rental procedures.",
    },
    {
      title: "Pre-departure briefing",
      description:
        "We provide comprehensive guidance before your journey, including travel tips, cultural orientation, academic expectations, and essential preparations to help you settle in confidently and smoothly.",
    },
  ];

  const process = [
    {
      step: "01",
      title: "Initial Consultation",
      description:
        "We assess your academic background, career goals, and preferences to recommend the best programs and destinations.",
    },
    {
      step: "02",
      title: "Program Selection",
      description:
        "Together, we identify universities and courses that align with your aspirations and budget.",
    },
    {
      step: "03",
      title: "Application Support",
      description:
        "We help prepare all required documents, essays, and applications to maximize your admission chances.",
    },
    {
      step: "04",
      title: "Visa Processing",
      description:
        "Complete assistance with visa documentation, interview preparation, and submission.",
    },
    {
      step: "05",
      title: "Pre-Departure",
      description:
        "Comprehensive briefing on accommodation, travel, cultural adaptation, and what to expect.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Study Abroad"
        description="Expert study abroad services for students seeking education in Australia, UK, Canada, New Zealand, and Europe. Complete support for university applications, visas, and more."
        ogTitle="Study Abroad Services - Expert Guidance for International Education"
        ogDescription="Comprehensive study abroad services including university selection, visa processing, and pre-departure briefing. Study in Australia, UK, Canada, New Zealand, or Europe."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-poppins text-4xl md:text-6xl font-bold tracking-tight leading-tight antialiased mb-4 text-white">
            Study Abroad Services
          </h1>
          <p className="font-jakarta text-lg md:text-xl tracking-tight leading-relaxed antialiasedmx-auto text-white">
            We provide comprehensive support for students aspiring to pursue
            their education abroad. From selecting the right program to securing
            your visa, we guide you through every step of your academic journey
            with expertise and personalized attention.
          </p>
        </div>
      </div>

      {/* Overview Section */}

      {/* Destinations Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Study Destinations
            </h2>
            <p className="font-jakarta text-lg text-gray-600 mx-auto tracking-tight leading-relaxed antialiased">
              Transform Your Future Through Education. We help you gain
              admission into universities across
            </p>
          </motion.div>

          <div className="space-y-8">
            {destinations.map((destination, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col md:flex-row"
              >
                <div className="w-full md:w-48 h-48 md:h-48 p-4">
                  <img
                    src={destination.image}
                    alt={destination.country}
                    className="w-full h-full object-contain rounded-xl"
                  />
                </div>
                <div className="p-6 md:p-8 flex-1">
                  <h3 className="font-poppins text-2xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
                    {destination.country}
                  </h3>
                  <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                    {destination.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Services Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Our Support Includes
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supportServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[#800020] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-poppins text-lg font-bold text-gray-900 mb-2 tracking-tight antialiased">
                      {service.title}
                    </h3>
                    <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Our Process
            </h2>
            <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
              A clear roadmap to studying abroad
            </p>
          </motion.div>

          <div className="space-y-6">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#800020] to-[#600018] flex items-center justify-center">
                      <span className="font-poppins text-white text-xl font-bold">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-poppins text-2xl font-bold text-gray-900 mb-2 tracking-tight antialiased">
                      {item.title}
                    </h3>
                    <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#004E89] to-[#800020]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight antialiased">
              Ready to Begin Your Journey?
            </h2>
            <p className="font-jakarta text-lg md:text-xl text-white/90 mb-8 tracking-tight antialiased">
              Let's discuss your study abroad goals and create a personalized
              plan for your success.
            </p>
            <Button
              variant="primary"
              size="lg"
              rightIcon={<ArrowRight className="w-5 h-5" />}
              onClick={() =>
                openWhatsApp(
                  "Hello! I'm interested in study abroad services. Please assist me with applications and next steps.\n\nName:\nEmail:\nPhone:\nDestination/Program:",
                )
              }
            >
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
